import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { format, startOfDay } from "date-fns";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as yup from "yup";
import { useMultiProviderAppointmentDrawerContext } from "@/components/booking/multiProviderAppointmentDrawer/multiProviderAppointmentDrawerContext";
import { ResponsiveConfirmDialog } from "@/components/common/modals/responsiveConfirmDialog";
import { DATE_FORMATS } from "@/config";
import { useMedspaId } from "@/hooks/common/useMedspaId";
import { WaitlistActions } from "@/hooks/waitlist/useAddToWaitlistDialog";
import { TimeOfDay, WaitlistSource } from "@/types";
import useErrorLogger from "@/utils/useErrorLogger";
import TimeOfDaySelector, {
  TimeOfDaySelectorFormValues,
} from "./timeOfDaySelector";

const MODAL_CONFIRM_OPTIONS = {
  confirmButtonProps: {
    size: "small" as const,
    sx: { height: "40px", borderRadius: "12px" },
  },
  discardButtonProps: {
    size: "small" as const,
    sx: { height: "40px", borderRadius: "12px", borderWidth: "1.5px" },
  },
};

const schema = yup.object({
  selectedTimeOfDay: yup
    .array()
    .of(yup.string().required())
    .min(1, "At least one time preference must be selected."),
});

type Props = {
  waitlist: WaitlistActions;
  currentAppointmentDate?: Date;
};

export default function AddToWaitlistDialog({
  waitlist,
  currentAppointmentDate,
}: Props) {
  const { client, startDate, appointmentServices } =
    useMultiProviderAppointmentDrawerContext();
  const medspaId = useMedspaId();
  const logError = useErrorLogger();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<TimeOfDaySelectorFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      selectedTimeOfDay: [
        TimeOfDay.MORNING,
        TimeOfDay.AFTERNOON,
        TimeOfDay.EVENING,
      ],
    },
    mode: "onChange",
  });

  if (!client) {
    return null;
  }

  const clientId = client.id;
  const clientName = `${client.firstName} ${client.lastName}`;
  const appointmentDate = currentAppointmentDate || startDate;
  const {
    isWaitlistDialogOpen: isModalOpen,
    closeWaitlistDialog: onClose,
    handleWaitlistSuccess: onSuccess,
    addToWaitlist,
    isLoadingWaitlist: isLoading,
  } = waitlist;

  async function onSubmit(data: TimeOfDaySelectorFormValues) {
    if (!medspaId) {
      toast.error("Medspa information is missing. Please try again.");

      return;
    }

    const toastId = toast.loading("Adding client to waitlist...");

    try {
      const desiredTime = data.selectedTimeOfDay;
      const waitlistServices = appointmentServices
        .map((service, index) => ({
          serviceMenuItemId: service.serviceMenuItem.id,
          providerId: service.provider?.id || undefined,
          order: index + 1,
        }))
        .filter((service) => service.providerId);

      await addToWaitlist({
        variables: {
          clientId,
          medspaId,
          desiredDate: format(
            startOfDay(appointmentDate),
            DATE_FORMATS.SERVER_DATE
          ),
          desiredTime,
          note: "",
          source: WaitlistSource.MOXIE_SUITE,
          waitlistServices,
        },
      });

      toast.success(`${clientName} was added to the waitlist!`, {
        id: toastId,
      });

      onSuccess();
      onClose();
    } catch (error) {
      logError(`Error creating waitlist entry: ${error}`);
      toast.error("Failed to add client to waitlist. Please try again.", {
        id: toastId,
      });
    }
  }

  return (
    <ResponsiveConfirmDialog
      isOpen={isModalOpen}
      title="Add client to waitlist"
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      submitText={isLoading ? "Adding..." : "Confirm & add"}
      isConfirmDisabled={!isValid || isLoading}
      modalProps={{ sx: { py: 2, width: "100%" }, contentOverflow: false }}
      modalConfirmOptions={MODAL_CONFIRM_OPTIONS}
      drawerProps={{
        dialogActionsProps: {
          sx: {
            pt: 0,
          },
        },
      }}
    >
      <Stack spacing={2}>
        <Typography color="text.secondary">
          You are adding {clientName} to the waitlist on{" "}
          <Typography
            component="span"
            variant="subtitleDefault"
            color="text.primary"
          >
            {format(appointmentDate, DATE_FORMATS.DATE_PICKER)}
          </Typography>
          . You can specify the time of day below if applicable
        </Typography>
        <TimeOfDaySelector control={control} errors={errors} />
      </Stack>
    </ResponsiveConfirmDialog>
  );
}
